import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import { Icon, Button, Input } from 'semantic-ui-react';
import { GoogleDriveOAuthSetup } from "./googleDrive/GoogleDriveOAuthSetup";
import { SlackConnect } from "./SlackConnect";
import { WhatsappConnect } from "./Whatsapp/WhatsappConnect";
import SchedulingConnect from "./scheduling/SchedulingConnect";
const integrations = [
    {
        id: 'gdrive',
        title: 'Google Drive',
        icon: 'google drive',
        iconColor: 'blue',
        description: 'Store and manage form submissions in Google Drive',
        category: 'Storage',
        component: React.createElement(GoogleDriveOAuthSetup, null),
    },
    {
        id: 'slack',
        title: 'Slack',
        icon: 'slack',
        iconColor: 'purple',
        description: 'Enable form filling in Slack',
        category: 'Communication',
        component: React.createElement(SlackConnect, null),
    },
    {
        id: 'whatsapp',
        title: 'WhatsApp Business',
        icon: 'whatsapp',
        iconColor: 'green',
        description: 'Connect your Whatsapp Business account to enable form submissions through WhatsApp.',
        category: 'Communication',
        component: React.createElement(WhatsappConnect, null),
    },
    {
        id: 'scheduling',
        title: 'Scheduling',
        icon: 'calendar',
        iconColor: 'orange',
        description: 'Manage calendar and appointments with Nylas',
        category: 'Productivity',
        component: React.createElement(SchedulingConnect, null)
    }
];
export function IntegrationsPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [hasChanges, setHasChanges] = useState({});
    // Extract slug from URL pathname
    // Handles both /integrations/slug and /integrations/slug/nested-routes
    const slugFromUrl = useMemo(() => {
        const pathParts = location.pathname.split('/').filter(Boolean);
        const integrationsIndex = pathParts.findIndex(part => part === 'integrations');
        if (integrationsIndex !== -1 && pathParts[integrationsIndex + 1]) {
            const potentialSlug = pathParts[integrationsIndex + 1];
            // Check if this slug matches any of our integrations
            const matchingIntegration = integrations.find(i => i.id === potentialSlug);
            if (matchingIntegration) {
                return potentialSlug;
            }
        }
        return null;
    }, [location.pathname]);
    // Find integration by slug from URL, or default to first integration
    const selectedIntegration = useMemo(() => {
        if (slugFromUrl) {
            const found = integrations.find(i => i.id === slugFromUrl);
            if (found)
                return found.id;
        }
        return integrations[0]?.id || 'gdrive';
    }, [slugFromUrl]);
    // Update URL when integration is selected
    const handleIntegrationSelect = (integrationId) => {
        const integration = integrations.find(i => i.id === integrationId);
        if (integration) {
            navigate(`/integrations/${integration.id}`, { replace: true });
        }
    };
    // Initialize URL if no slug is present
    useEffect(() => {
        if (!slugFromUrl) {
            navigate(`/integrations/${integrations[0].id}`, { replace: true });
        }
    }, [slugFromUrl, navigate]);
    const iconColorClasses = {
        blue: { text: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
        purple: { text: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
        green: { text: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
        orange: { text: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' }
    };
    // Filter integrations based on search query
    const filteredIntegrations = useMemo(() => {
        if (!searchQuery.trim())
            return integrations;
        const query = searchQuery.toLowerCase();
        return integrations.filter(integration => integration.title.toLowerCase().includes(query) ||
            integration.description.toLowerCase().includes(query) ||
            integration.category.toLowerCase().includes(query));
    }, [searchQuery]);
    // Get categories
    const categories = useMemo(() => {
        const cats = Array.from(new Set(integrations.map(i => i.category)));
        return cats;
    }, []);
    const currentIntegration = integrations.find(i => i.id === selectedIntegration) || integrations[0];
    return (React.createElement(PageLayout, { title: "Integrations", subtitle: "Connect your organization with external services and platforms", icon: "plug", variant: "wide" },
        React.createElement("div", { className: "space-y-6" },
            React.createElement("div", { className: "bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" },
                React.createElement("div", { className: "flex h-[calc(100vh-300px)] min-h-[600px]" },
                    React.createElement("div", { className: "w-80 border-r border-gray-200 bg-gray-50 flex flex-col" },
                        React.createElement("div", { className: "p-4 border-b border-gray-200 bg-white" },
                            React.createElement(Input, { placeholder: "Search integrations...", value: searchQuery, onChange: (e, { value }) => setSearchQuery(value), icon: "search", iconPosition: "left", className: "!w-full" })),
                        React.createElement("div", { className: "flex-1 overflow-y-auto" },
                            categories.map(category => {
                                const categoryIntegrations = filteredIntegrations.filter(i => i.category === category);
                                if (categoryIntegrations.length === 0)
                                    return null;
                                return (React.createElement("div", { key: category, className: "mb-4" },
                                    React.createElement("div", { className: "px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider" }, category),
                                    categoryIntegrations.map(integration => {
                                        const isSelected = selectedIntegration === integration.id;
                                        const intIconStyles = iconColorClasses[integration.iconColor] || iconColorClasses.blue;
                                        return (React.createElement("button", { key: integration.id, onClick: () => handleIntegrationSelect(integration.id), className: `w-full px-4 py-3 flex items-center gap-3 text-left transition-colors ${isSelected
                                                ? 'bg-white border-l-4 border-orange-500 text-gray-900'
                                                : 'text-gray-700 hover:bg-gray-100'}` },
                                            React.createElement("div", { className: `w-10 h-10 rounded-lg ${intIconStyles.bg} flex items-center justify-center flex-shrink-0` },
                                                React.createElement(Icon, { name: integration.icon, className: `${intIconStyles.text} !m-0` })),
                                            React.createElement("div", { className: "flex-1 min-w-0" },
                                                React.createElement("div", { className: "flex items-center gap-2" },
                                                    React.createElement("span", { className: `font-medium text-sm ${isSelected ? 'text-gray-900' : 'text-gray-700'}` }, integration.title)))));
                                    })));
                            }),
                            filteredIntegrations.length === 0 && (React.createElement("div", { className: "p-8 text-center" },
                                React.createElement(Icon, { name: "search", size: "large", className: "!text-gray-300 !m-0 mb-2" }),
                                React.createElement("p", { className: "text-sm text-gray-500" }, "No integrations found"),
                                React.createElement("p", { className: "text-xs text-gray-400 mt-1" }, "Try a different search term"))))),
                    React.createElement("div", { className: "flex-1 overflow-y-auto bg-white" },
                        React.createElement("div", { className: "p-8" },
                            React.createElement("div", { className: "grid grid-cols-1 \n              gap-8" },
                                React.createElement("div", { className: "space-y-4" },
                                    React.createElement("div", { className: "flex items-center justify-between" },
                                        React.createElement("div", null,
                                            React.createElement("h3", { className: "text-xl font-bold text-gray-900 mb-1" }, currentIntegration.title),
                                            React.createElement("p", { className: "text-sm text-gray-600" }, currentIntegration.description))),
                                    React.createElement("div", { className: "bg-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm" }, currentIntegration.component)),
                                currentIntegration?.config && React.createElement("div", { className: "space-y-4" },
                                    React.createElement("h3", { className: "text-xl font-bold text-gray-900" }, "Configuration"),
                                    React.createElement("div", { className: "bg-white rounded-xl p-6 border border-gray-200 shadow-sm" },
                                        currentIntegration?.config,
                                        React.createElement("div", { className: "mt-8 pt-6 border-t border-gray-200 flex items-center justify-between" },
                                            React.createElement(Button, { basic: true, onClick: () => {
                                                    setHasChanges({ ...hasChanges, [currentIntegration.id]: false });
                                                }, className: "!text-gray-600 hover:!bg-gray-50" },
                                                React.createElement(Icon, { name: "refresh" }),
                                                "Reset to Defaults"),
                                            React.createElement("div", { className: "flex gap-3" },
                                                React.createElement(Button, { basic: true, onClick: () => {
                                                        setHasChanges({ ...hasChanges, [currentIntegration.id]: false });
                                                    }, className: "!text-gray-600 hover:!bg-gray-50" }, "Cancel"),
                                                React.createElement(Button, { primary: true, onClick: () => {
                                                        setHasChanges({ ...hasChanges, [currentIntegration.id]: false });
                                                    }, className: "!bg-orange-500 hover:!bg-orange-600 !text-white" },
                                                    React.createElement(Icon, { name: "save" }),
                                                    "Save Configuration")))))))))))));
}
